/**
 * Fragment de routes de la vague V8e (paquet v8e-p00-socle ; FICHE-CONTRATS-V8E.md § 3), rendu par le registre v10.
 *
 * Les 25 routes sont déclarées dans `ecrans/v8e/commun/routes.ts` ; leurs modules d'écran appartiennent aux paquets
 * p01 à p07 et sont chargés à la demande (`import.meta.glob` NON eager : aucun code d'écran dans le paquet principal).
 * Une route dont le module n'existe pas encore n'est pas déclarée : l'adresse reste servie par le socle (routes
 * `remplace`) ou inconnue (routes neuves), jamais par un écran vide. Aucun appel réseau ni effet au chargement.
 */
import { lazy, Suspense, type ComponentType, type JSX } from 'react'

import type { Session } from '../../auth/session'
import { ROUTES_V8E, cleTitreEcran } from '../ecrans/v8e/commun/routes'
import type { ContexteRoutes, DeclarationRoute, FragmentRoutes } from './registre'

export type ModuleEcranV8e = { default: ComponentType<{ session: Session }> }
export type ChargeursV8e = Record<string, () => Promise<ModuleEcranV8e>>

/** Clé du chargeur d'un module de la table (`projets/ProjetsOrganisation` → `../ecrans/v8e/projets/ProjetsOrganisation.tsx`). */
export function cleModuleV8e(module: string): string {
  return `../ecrans/v8e/${module}.tsx`
}

function ecran(identifiant: string, Composant: ComponentType<{ session: Session }>) {
  return ({ session, t }: ContexteRoutes): JSX.Element => (
    <Suspense fallback={
      <p role="status">{t('v8e.commun.etat.chargement', undefined, { quoi: t(cleTitreEcran(identifiant)) })}</p>
    }>
      <Composant session={session} />
    </Suspense>
  )
}

/** Déclarations des routes dont le module est présent, dans l'ordre de la table. */
export function routesV8e(chargeurs: ChargeursV8e): DeclarationRoute[] {
  return ROUTES_V8E.flatMap(({ ecran: identifiant, chemin, module, remplace }) => {
    const charger = chargeurs[cleModuleV8e(module)]
    if (!charger) return []
    const element = ecran(identifiant, lazy(charger))
    return [remplace ? { chemin, element, remplace } : { chemin, element }]
  })
}

const CHARGEURS: ChargeursV8e = import.meta.glob<ModuleEcranV8e>([
  '../ecrans/v8e/*/*.tsx', '!../ecrans/v8e/commun/*', '!../ecrans/v8e/**/*.test.tsx',
])

export const fragment: FragmentRoutes = { zone: 'v8e', routes: routesV8e(CHARGEURS) }
